import { useSyncExternalStore } from 'react';

import type { OrganizationMembership } from './auth';

const STORAGE_KEY = 'partforge.activeOrgSlug';
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function readSlug(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActiveOrgSlug(slug: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (slug === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* ignore quota errors etc. */
  }
  notify();
}

/**
 * Returns the slug to use for org-scoped data fetches. Falls back to the
 * first org in the list whenever the stored slug isn't valid (org left,
 * org deleted, no orgs yet, etc.).
 */
export function useActiveOrgSlug(
  orgs: OrganizationMembership[] | undefined,
): string | undefined {
  const stored = useSyncExternalStore(
    subscribe,
    readSlug,
    () => null,
  );
  if (!orgs || orgs.length === 0) return undefined;
  if (stored && orgs.some((o) => o.slug === stored)) return stored;
  return orgs[0].slug;
}

export function useActiveOrg(
  orgs: OrganizationMembership[] | undefined,
): OrganizationMembership | undefined {
  const slug = useActiveOrgSlug(orgs);
  return orgs?.find((o) => o.slug === slug);
}
